import {
  GPS_FIX_MAX_PLAUSIBLE_GROUND_SPEED_KMH,
  GPS_FIX_MAX_UNEXPLAINED_DISPLACEMENT_M,
  GPS_FIX_MAX_USABLE_DOP,
  GPS_FIX_RESYNC_S,
  GPS_FIX_TRUSTED_SPEED_TO_TRACK_RATIO,
  GPS_FIX_MIN_MOVING_SPEED_KMH,
} from '../analysis/config/gpsFixGate'
import { calculateDistance } from '../utils/distanceUtils'

export interface GpsFixReading {
  timeMs: number
  latitude: number
  longitude: number
  speedKmh?: number
  dop?: number
}

const isOnTheGlobe = ({ latitude, longitude }: GpsFixReading): boolean =>
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  Math.abs(latitude) <= 90 &&
  Math.abs(longitude) <= 180 &&
  !(latitude === 0 && longitude === 0)

const isPreciseEnough = ({ dop }: GpsFixReading): boolean =>
  dop === undefined || dop <= GPS_FIX_MAX_USABLE_DOP

const isUsable = (fix: GpsFixReading): boolean =>
  Number.isFinite(fix.timeMs) && isOnTheGlobe(fix) && isPreciseEnough(fix)

const hasSpeed = (
  fix: GpsFixReading
): fix is GpsFixReading & {
  speedKmh: number
} => fix.speedKmh !== undefined && Number.isFinite(fix.speedKmh)

const secondsBetween = (from: GpsFixReading, to: GpsFixReading): number =>
  (to.timeMs - from.timeMs) / 1000

const metresBetween = (from: GpsFixReading, to: GpsFixReading): number =>
  calculateDistance(from.latitude, from.longitude, to.latitude, to.longitude)

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

const movingPairSpeedToTrackRatios = (fixes: GpsFixReading[]): number[] => {
  const ratios: number[] = []
  for (let i = 1; i < fixes.length; i++) {
    const [from, to] = [fixes[i - 1], fixes[i]]
    if (!hasSpeed(from) || !hasSpeed(to)) continue
    const seconds = secondsBetween(from, to)
    const isMoving =
      Math.min(from.speedKmh, to.speedKmh) >= GPS_FIX_MIN_MOVING_SPEED_KMH
    if (!isMoving || seconds <= 0 || seconds > GPS_FIX_RESYNC_S) continue
    const speedMetres = ((from.speedKmh + to.speedKmh) / 2 / 3.6) * seconds
    ratios.push(speedMetres / metresBetween(from, to))
  }
  return ratios
}

const reportedSpeedMatchesTrack = (fixes: GpsFixReading[]): boolean => {
  const ratios = movingPairSpeedToTrackRatios(fixes)
  if (ratios.length === 0) return false
  const typicalRatio = median(ratios)
  return (
    typicalRatio >= GPS_FIX_TRUSTED_SPEED_TO_TRACK_RATIO.min &&
    typicalRatio <= GPS_FIX_TRUSTED_SPEED_TO_TRACK_RATIO.max
  )
}

const explainingSpeedKmh = (
  anchor: GpsFixReading,
  fix: GpsFixReading,
  speedIsTrusted: boolean
): number => {
  const reported = [anchor, fix].filter(hasSpeed).map((f) => f.speedKmh)
  return speedIsTrusted && reported.length > 0
    ? Math.max(...reported)
    : GPS_FIX_MAX_PLAUSIBLE_GROUND_SPEED_KMH
}

const isStale = (anchor: GpsFixReading, fix: GpsFixReading): boolean =>
  secondsBetween(anchor, fix) > GPS_FIX_RESYNC_S

const isReachable = (
  anchor: GpsFixReading,
  fix: GpsFixReading,
  speedIsTrusted: boolean
): boolean => {
  const reachableMetres =
    (explainingSpeedKmh(anchor, fix, speedIsTrusted) / 3.6) *
      secondsBetween(anchor, fix) +
    GPS_FIX_MAX_UNEXPLAINED_DISPLACEMENT_M
  return metresBetween(anchor, fix) <= reachableMetres
}

export function rejectImplausibleGpsFixes<T>(
  samples: T[],
  read: (sample: T) => GpsFixReading
): T[] {
  const fixes = samples.map(read)
  const speedIsTrusted = reportedSpeedMatchesTrack(fixes.filter(isUsable))
  const kept: T[] = []
  let anchor: GpsFixReading | null = null

  for (let i = 0; i < fixes.length; i++) {
    const fix = fixes[i]
    if (!isUsable(fix)) continue

    if (anchor !== null && fix.timeMs <= anchor.timeMs) {
      kept.push(samples[i])
      continue
    }

    if (
      anchor === null ||
      isStale(anchor, fix) ||
      isReachable(anchor, fix, speedIsTrusted)
    ) {
      anchor = fix
      kept.push(samples[i])
    }
  }

  return kept.length === samples.length ? samples : kept
}
